export interface FlagPanelMeta {
  /** Shown on hover of the flag key. */
  description?: string;
  /**
   * Values the non-boolean override select offers. These are local-override presets
   * (a local override isn't restricted to Datadog's registered variants), so add any
   * value worth testing. Absent or single-value renders read-only.
   */
  options?: readonly (string | number | object)[];
}

/**
 * Display metadata for the debug panel's flag list, keyed by Datadog flag key. Datadog
 * doesn't send a flag's description or its full option set to the client (only the
 * assigned variation), so the panel sources both from here. Not a source of flag values
 * or a registry of what's live — it only affects how a flag renders in the panel.
 */
export const FLAG_METADATA: Record<string, FlagPanelMeta> = {
  'debug-panel': {
    description:
      'Gates the emergency_data debug panel. Off in production, on elsewhere.',
  },
  show_recent_transcripts: {
    description: 'Show recent transcripts in the event pane / queue.',
  },
  ttvt_timeouts: {
    description:
      'TTVT transcription timeout config, e.g. { timeout_minutes: 30 }.',
    options: [{ timeout_minutes: 30 }],
  },
  ttvt_tts_model: {
    description: 'TTVT text-to-speech model name.',
    options: ['eleven_multilingual_v2'],
  },
  admin_call_indicator: {
    description: 'Admin-line call indicator in the queue.',
  },
  field_share_iar: {
    description: 'Field Share to IamResponding (IAR).',
  },
  'alert-field-sharing': {
    description:
      'Field Share for alerts — the Share button on the alert card. Gates alerts only; call sharing stays on unite_sharing.',
  },
  'towpro-force-enabled': {
    description:
      'Bypasses the TowPro capability and permission gates so the tow request feature shows for every agency and user.',
  },
  reset_keyword_defaults: {
    description: 'Reset keyword defaults (admin keywords).',
  },
  mg_home_radius: {
    description: 'Medical Guardian home-radius map overlay.',
  },
  use_default_google_geocoder: {
    description:
      'Prefer Google over Esri for reverse geocoding (GIS > Google > Esri).',
  },
  basemap_sublayer: {
    description: 'Basemap sublayer toggle in the basemap menu.',
  },
  google_advanced_markers: {
    description: 'Render map call markers with Google Advanced Markers.',
  },
  manage_landing_zones: {
    description: 'Manage Landing Zones admin section (sidebar gate).',
  },
  'tt911-transfer-to-agency': {
    description: 'Text-to-911 transfer-to-agency button.',
  },
  'rti-collaborator-chat': {
    description: 'Collaborator chat parity for RTI-downstream (pushed) calls.',
  },
};
